import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { MagnifyingGlass, ArrowRight, Package } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";
import { Reveal } from "@/components/site/Reveal";
import TrackingTimeline from "@/components/site/TrackingTimeline";

export default function TrackPage() {
  const [params] = useSearchParams();
  const [number, setNumber] = useState(params.get("number") || "");
  const [shipment, setShipment] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const search = async (num) => {
    if (!num.trim()) return;
    setLoading(true);
    setError("");
    setShipment(null);
    try {
      const { data } = await api.get(`/track/${encodeURIComponent(num.trim())}`);
      setShipment(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Not found");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const n = params.get("number");
    if (n) search(n);
  }, []);

  return (
    <div className="min-h-screen pt-36 pb-28 max-w-4xl mx-auto px-6">
      <Reveal>
        <p className="font-mono text-xs tracking-[0.3em] uppercase text-signal mb-4">Live Tracking</p>
        <h1 className="font-display font-semibold text-5xl md:text-7xl tracking-tighter leading-[0.92]">
          Where&apos;s my parcel?
        </h1>
      </Reveal>

      <form
        onSubmit={(e) => { e.preventDefault(); search(number); }}
        className="mt-10 flex flex-col sm:flex-row bg-white border-2 border-obsidian shadow-brutal"
        data-testid="track-form"
      >
        <div className="flex items-center gap-3 px-5 flex-1">
          <MagnifyingGlass weight="bold" className="text-neutral-400" size={22} />
          <input
            data-testid="track-input"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            placeholder="Enter tracking number (e.g. MP100001)"
            className="w-full py-4 outline-none font-mono tracking-wide text-obsidian bg-transparent"
          />
        </div>
        <button data-testid="track-btn" type="submit"
          className="group bg-signal text-white font-medium px-8 py-4 hover:bg-obsidian transition-colors flex items-center justify-center gap-2">
          {loading ? "Searching..." : "Track"} <ArrowRight weight="bold" className="group-hover:translate-x-1 transition-transform" />
        </button>
      </form>

      <div className="mt-10">
        {error && (
          <div className="border-2 border-obsidian bg-white p-10 text-center" data-testid="track-error">
            <Package weight="duotone" size={48} className="text-neutral-300 mx-auto" />
            <p className="font-display font-medium text-2xl mt-4 text-obsidian">No parcel found</p>
            <p className="font-sans text-neutral-500 mt-2">{error}. Double-check the number and try again.</p>
          </div>
        )}
        {shipment && <TrackingTimeline shipment={shipment} />}
      </div>
    </div>
  );
}
